import React, { useState } from "react";
import { Button, Modal } from "antd";
import { useLoader } from "../../../../../Context/LoaderContext";
import { Cart, CartSummary } from "../../../../../Service/cart.service";
import useOrganization from "../../../../../Hooks/useOrganization";
import { Plan } from "../../../../../Entities/Plan";
import { openNotification } from "../../../../../Components/Notification";
import SaveGSTModal from "./SaveGSTModal";
import {
  SubscriptionDto,
  upgradeSubscriptionDto,
} from "../../../../../Dto/Subscription.dto";
import { useAppContext } from "../../../../../Context/AppContext/AppContext";
import { Subscription } from "../../../../../Entities/Subscription";
import useRazorpay from "react-razorpay";
import { appName, appLogo } from "../../../../../Utils/assets.constants";
import useUser from "../../../../../Hooks/useUser";
import paymentService from "../../../../../Service/payment.service";
import subscriptionPurchaseService from "../../../../../Service/subscription.purchase.service";
import { useNavigate } from "react-router-dom";

interface PayNowButtonProps {
  mode: "upgrade" | "addon_only" | "renew";
  disabled: boolean;
  summary: CartSummary;
  userCurrentPlan: Plan | null;
  userCurrentSubscription: Subscription | null;
  userCart: Cart;
}

const PayNowButton: React.FC<PayNowButtonProps> = ({
  mode,
  disabled,
  summary,
  userCurrentPlan,
  userCurrentSubscription,
  userCart,
}) => {
  const { isLoading, setLoading, setLoadingMessage } = useLoader();
  const { currentRegion } = useAppContext();
  const { org } = useOrganization();
  const { user } = useUser();
  const navigate = useNavigate();

  const [Razorpay] = useRazorpay();

  const [showGSTModal, setShowGSTModal] = useState<boolean>(false);

  const userCurrencyCode = currentRegion === "IND" ? "INR" : "USD";

  const handlePaymentSuccess = (subscriptionId: string) => {
    openNotification("success", "Payment Successful", `${subscriptionId}`);
    navigate(`/transaction-details?subscriptionId=${subscriptionId}`, {
      replace: true,
    });
  };

  const handlePaymentFailure = () => {
    openNotification("error", "Payment Failed", "Please try again");
  };

  const handleRazorpayPayment = async (
    paymentGatewaySubscriptionId: string,
    newSubscriptionId: string,
  ) => {
    const razorpayKey = process.env.REACT_APP_RZP_KEY || null;
    if (!razorpayKey) {
      openNotification("error", "Razorpay Key Not Found", "Please try again");
      return;
    }

    if (!user) {
      openNotification("error", "User Not Found", "Please try again");
      return;
    }

    if (!org) {
      openNotification("error", "Organization Not Found", "Please try again");
      return;
    }

    if (!summary) {
      openNotification("error", "Cart Summary Not Found", "Please try again");
      return;
    }

    try {
      const options: any = {
        key: razorpayKey,
        subscription_id: paymentGatewaySubscriptionId,
        name: appName,
        image: appLogo,
        description: "QualifyMe.ai subscription purchase payment.",
        prefill: {
          name: user.firstName + " " + user.lastName,
          email: user.email,
        },
        theme: {
          hide_topbar: true,
          color: org.theme.primaryColor,
        },
        modal: {
          backdropclose: false,
          escape: false,
          confirm_close: true,
          ondismiss: () => {
            openNotification("warning", "Payment Cancelled", "");
          },
        },
        handler: async function (response: any) {
          setLoading(true);
          setLoadingMessage("Verifying payment...");

          const success = await paymentService.verifyPayment(response);

          setLoading(false);
          setLoadingMessage(null);

          if (success) {
            handlePaymentSuccess(newSubscriptionId);
          } else {
            handlePaymentFailure();
          }
        },
      };

      const rzpay = new Razorpay(options);
      rzpay.on("payment.failed", function (response: any) {
        console.error("Payment failed: ", response);
        handlePaymentFailure();
      });
      rzpay.open();
    } catch (error: any) {
      console.error("Error in handleRazorpayPayment: ", error);
      openNotification(
        "error",
        "Error in handleRazorpayPayment",
        "Please try again",
      );
      return;
    }
  };

  const handleSubscriptionBuy = async (payLoad: SubscriptionDto) => {
    try {
      setLoading(true);
      setLoadingMessage("Initiating subscription purchase...");
      const data =
        await subscriptionPurchaseService.buyNewSubscription(payLoad);
      if (!data) {
        setLoading(false);
        setLoadingMessage(null);
        openNotification(
          "error",
          "Subscription Purchase Failed",
          "Please try again",
        );
        return;
      }
      setLoading(false);
      setLoadingMessage(null);
      return await handleRazorpayPayment(data.pgSubscriptionId, data._id);
    } catch (error: any) {
      setLoading(false);
      setLoadingMessage(null);
      console.error("Error in handleSubscriptionBuy: ", error);
      openNotification(
        "error",
        "Error in handleSubscriptionBuy",
        "Please try again",
      );
      return;
    }
  };

  const handleSubscriptionUpgrade = async (payLoad: upgradeSubscriptionDto) => {
    try {
      setLoading(true);
      setLoadingMessage("Upgrading subscription...");
      const data =
        await subscriptionPurchaseService.upgradeSubscription(payLoad);

      if (!data) {
        setLoading(false);
        setLoadingMessage(null);
        openNotification(
          "error",
          "Subscription Upgrade Failed",
          "Please try again",
        );
        return;
      }

      return await handleRazorpayPayment(data.pgSubscriptionId, data._id);
    } catch (error: any) {
      setLoading(false);
      setLoadingMessage(null);
      console.error("Error in handleSubscriptionUpgrade: ", error);
      openNotification(
        "error",
        "Error in handleSubscriptionUpgrade",
        "Please try again",
      );
      return;
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleSubscriptionPurchase = async (productSummary: CartSummary) => {
    try {
      // validating cart summary
      if (!productSummary.selectedPlan) {
        openNotification(
          "error",
          "Selected Plan Not Found",
          "Please try again",
        );
        return;
      }

      // determining billing period for addons configuring as monthly
      const billing =
        productSummary.selectedPlan.type === "addon"
          ? "month"
          : productSummary.billingPeriod;

      // determining quantity
      const quantity =
        productSummary.selectedPlan.type === "base"
          ? 1
          : userCart.addons.find((add: any) => add.quantity > 0)?.quantity || 0;

      // validating quantity
      if (!quantity || quantity <= 0) {
        openNotification("error", "Quantity Not Found", "Please try again");
        return;
      }

      // preparing payload for subscription creation
      const payLoad: SubscriptionDto = {
        planId: productSummary?.selectedPlan?._id || "",
        quantity: quantity,
        subscriptionPeriod: billing,
        addons:
          productSummary.selectedPlan.type === "addon" ? [] : userCart.addons,
        offerId: productSummary?.appliedOffer
          ? productSummary.appliedOffer._id
          : undefined,
        currency: userCurrencyCode,
      };

      const isUpgrading =
        mode === "upgrade" &&
        userCurrentSubscription &&
        userCurrentPlan?.type !== "free";

      // handling subscription upgrade or purchase
      if (isUpgrading) {
        const upgradePayload: upgradeSubscriptionDto = {
          newPlanDetails: payLoad,
          subscriptionId: userCurrentSubscription?._id,
        };

        Modal.confirm({
          title: "Are you sure you want to upgrade ?",
          centered: true,
          icon: null,
          content:
            "Upgrading your current plan will only take effect after the current month is completed, and a prorated amount will be charged now.",
          onOk: async () => {
            return await handleSubscriptionUpgrade(upgradePayload);
          },
          okText: "Upgrade Now",
          cancelText: "Cancel",
          onCancel: () => {
            openNotification("info", "Upgrade Request Canceled", "");
            return;
          },
        });
      } else {
        return await handleSubscriptionBuy(payLoad);
      }
    } catch (error: any) {
      console.error("Error in handleSubscriptionPurchase: ", error);
      openNotification(
        "error",
        "Error in handleSubscriptionPurchase",
        "Please try again",
      );
      return;
    }
  };

  const handlePaynowClick = async () => {
    if (!org) {
      openNotification("error", "Organization Not Found", "Please try again");
      return;
    }

    if (!userCurrentPlan) {
      openNotification("error", "Current Plan Not Found", "Please try again");
      return;
    }

    if (!summary) {
      openNotification("error", "Cart Summary Not Found", "Please try again");
      return;
    }

    if (!org?.gstNumber && currentRegion === "IND") {
      setShowGSTModal(true);
      return;
    }

    return await handleSubscriptionPurchase(summary);
  };

  const handleGSTModalProceed = async () => {
    if (!org) {
      openNotification("error", "Organization Not Found", "Please try again");
      return;
    }

    if (!userCurrentPlan) {
      openNotification("error", "Current Plan Not Found", "Please try again");
      return;
    }

    if (!summary) {
      openNotification("error", "Cart Summary Not Found", "Please try again");
      return;
    }

    return await handleSubscriptionPurchase(summary);
  };

  return (
    <div>
      <Button
        type="primary"
        size="middle"
        block
        style={{ maxWidth: "300px" }}
        disabled={disabled}
        onClick={handlePaynowClick}
        loading={isLoading}
      >
        Pay Now
      </Button>
      <SaveGSTModal
        open={showGSTModal}
        setOpen={setShowGSTModal}
        onProceed={handleGSTModalProceed}
      />
    </div>
  );
};

export default PayNowButton;
